"use client"

import { useQuery } from '@tanstack/react-query'
import { DriverErrorState, DriverLoadingState, DriverPanel, DriverPwaShell, DriverStatusBadge, fetchDriverProfile, formatDriverDateTime } from '../../../../lib/driverPwaUi'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'

type DriverOrderDetail = {
  id: string
  vehiclePlate: string | null
  status: string
  plannedPickupAt: string | null
  plannedDeliveryAt: string | null
  dispatcherNotes: string | null
  originLocationName: string | null
  destinationLocationName: string | null
  photos: Array<{
    id: string
    fileName: string
    imageUrl: string
  }>
}

async function fetchDriverOrderDetail(id: string): Promise<DriverOrderDetail> {
  const call = await apiCall<DriverOrderDetail>(`/api/road/dispatch/driver/orders/${encodeURIComponent(id)}`)
  if (!call.ok || !call.result) {
    throw new Error('Failed to load order detail.')
  }
  return call.result
}

export default function DriverOrderDetailPage({ params }: { params: { id: string } }) {
  const profileQuery = useQuery({
    queryKey: ['driver-pwa', 'profile'],
    queryFn: fetchDriverProfile,
    retry: false,
  })
  const detailQuery = useQuery({
    queryKey: ['driver-pwa', 'order-detail', params.id],
    queryFn: () => fetchDriverOrderDetail(params.id),
    retry: false,
  })

  if ((profileQuery.isLoading && !profileQuery.data) || (detailQuery.isLoading && !detailQuery.data)) {
    return <DriverLoadingState />
  }

  if (profileQuery.isError) {
    return <DriverErrorState title="Driver PWA is not available" description={profileQuery.error instanceof Error ? profileQuery.error.message : 'This account is not linked to an active driver profile.'} />
  }

  if (detailQuery.isError || !detailQuery.data) {
    return <DriverErrorState title="Order detail is unavailable" description={detailQuery.error instanceof Error ? detailQuery.error.message : 'Failed to load order detail.'} />
  }

  const detail = detailQuery.data

  return (
    <DriverPwaShell
      title="Order Detail"
      subtitle="Read-only detail for an assigned dispatch."
      profile={profileQuery.data ?? null}
    >
      <DriverPanel title="Dispatch" action={<DriverStatusBadge status={detail.status} />}>
        <div className="space-y-4">
          <div className="rounded-[22px] bg-white/6 px-4 py-4">
            <div className="text-sm font-medium">
              {detail.originLocationName ?? 'Origin pending'} to {detail.destinationLocationName ?? 'Destination pending'}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-[22px] bg-slate-950/45 px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Pickup</div>
              <div className="mt-2">{formatDriverDateTime(detail.plannedPickupAt)}</div>
            </div>
            <div className="rounded-[22px] bg-slate-950/45 px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Delivery</div>
              <div className="mt-2">{formatDriverDateTime(detail.plannedDeliveryAt)}</div>
            </div>
          </div>
          <div className="rounded-[22px] border border-white/8 bg-white/5 px-4 py-3 text-sm text-slate-200">
            Vehicle: {detail.vehiclePlate ?? 'Not assigned'}
          </div>
          {detail.dispatcherNotes ? (
            <div className="rounded-[22px] border border-amber-400/18 bg-amber-500/10 px-4 py-4 text-sm leading-6 text-amber-50">
              {detail.dispatcherNotes}
            </div>
          ) : null}
        </div>
      </DriverPanel>

      <DriverPanel eyebrow="Attachments" title="Photos">
        <div className="grid grid-cols-2 gap-3">
          {detail.photos.map((photo) => (
            <a key={photo.id} href={photo.imageUrl} target="_blank" rel="noreferrer" className="overflow-hidden rounded-[22px] border border-white/8 bg-white/5">
              <img src={photo.imageUrl} alt={photo.fileName} className="aspect-square h-full w-full object-cover" />
            </a>
          ))}
          {detail.photos.length === 0 ? (
            <div className="col-span-2 rounded-[22px] border border-dashed border-white/12 px-4 py-5 text-sm text-slate-300">
              No photos are attached to this order.
            </div>
          ) : null}
        </div>
      </DriverPanel>
    </DriverPwaShell>
  )
}
